/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.3
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import Collapse from "@mui/material/Collapse";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Icon from "@mui/material/Icon";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";

// Custom styles for the SidenavCollapse
import {
  collapseItem,
  collapseIconBox,
  collapseIcon,
  collapseText,
  collapseArrow,
} from "examples/Sidenav/styles/sidenavCollapse";

// Soft UI Dashboard PRO React context
import { useSoftUIController } from "context";

import { useSystemTheme } from "context/SystemThemeContext";

function SidenavCollapse({ 
  icon, 
  name, 
  children = false, 
  active = false, 
  noCollapse = false, 
  open = false, 
  ...rest 
}) {
  const { mode } = useSystemTheme();
  const isDarkMode = mode === "dark";
  const [controller] = useSoftUIController();
  const { miniSidenav, transparentSidenav, sidenavColor } = controller;

  const activeColor = isDarkMode ? "#219297" : undefined;

  return (
    <>
      <ListItem component="li">
        <SoftBox 
          {...rest} 
          sx={(themeCallback) => {
            const styles = collapseItem(themeCallback, { active, transparentSidenav });
            return {
              ...styles,
              background: active 
                ? (isDarkMode ? activeColor : styles.background)
                : (isDarkMode ? "transparent" : styles.background),
              color: active 
                ? "#ffffff"
                : (isDarkMode ? "#e2e8f0" : styles.color),
              "&:hover": {
                background: active 
                  ? (isDarkMode ? "#00695c" : undefined)
                  : (isDarkMode ? "#2d2d3f" : undefined),
              },
            };
          }}
        >
          <ListItemIcon
            sx={(themeCallback) => {
              const styles = collapseIconBox(themeCallback, { active, transparentSidenav, sidenavColor });
              return {
                ...styles,
                background: active 
                  ? "#ffffff"
                  : (isDarkMode ? "#060C18" : styles.background),
                "& svg, & svg g": {
                  fill: active 
                    ? (isDarkMode ? "#ffffff" : undefined)
                    : (isDarkMode ? "#060C18" : undefined),
                },
              };
            }}
          >
            {typeof icon === "string" ? (
              <Icon 
                sx={(themeCallback) => {
                  const styles = collapseIcon(themeCallback, { active });
                  return {
                    ...styles,
                    color: active 
                      ? (isDarkMode ? "#ffffff" : styles.color)
                      : (isDarkMode ? "#060C18" : styles.color),
                  };
                }}
              >
                {icon}
              </Icon>
            ) : (
              icon
            )}
          </ListItemIcon>

          <ListItemText
            primary={name}
            sx={(themeCallback) => {
              const styles = collapseText(themeCallback, { miniSidenav, transparentSidenav, active });
              return {
                ...styles,
                "& span": {
                  ...styles["& span"],
                  color: active 
                    ? (isDarkMode ? "#ffffff" : "#060C18")
                    : (isDarkMode ? "#e2e8f0" : styles["& span"]?.color),
                },
              };
            }}
          />

          {!noCollapse && (
            <Icon
              sx={(themeCallback) => {
                const styles = collapseArrow(themeCallback, { noCollapse, transparentSidenav, miniSidenav, open });
                return {
                  ...styles,
                  color: isDarkMode ? "#a0aec0" : styles.color,
                  transform: open ? "rotate(0)" : "rotate(-180deg)",
                };
              }}
            >
              expand_less
            </Icon>
          )}
        </SoftBox>
      </ListItem>
      {children && (
        <Collapse in={open} unmountOnExit>
          {children}
        </Collapse>
      )}
    </>
  );
}

// Typechecking props for the SidenavCollapse
SidenavCollapse.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark", "green", "teal"]),
  icon: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  children: PropTypes.node,
  active: PropTypes.bool,
  noCollapse: PropTypes.bool,
  open: PropTypes.bool,
};

export default SidenavCollapse;